import React, { useEffect, useState } from 'react';
import { Menu, X, Globe } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useLocation } from 'react-router-dom';
const menuLinks = [{
  label: 'Services',
  anchor: '#services'
}, {
  label: 'Why us',
  anchor: '#why-us'
}, {
  label: 'Case studies',
  anchor: '#case-studies'
}, {
  label: 'Knowledge hub',
  route: '/knowledge-hub'
}, {
  label: 'Contact',
  anchor: '#contact'
}];
export function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  // Close menu on route change
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);
  // Prevent body scroll when menu is open
  useEffect(() => {
    if (menuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [menuOpen]);
  const isHome = location.pathname === '/';
  const getHref = (link: (typeof menuLinks)[0]) => {
    if (link.route) return link.route;
    return isHome ? link.anchor : `/${link.anchor}`;
  };
  const handleLinkClick = () => {
    setMenuOpen(false);
  };
  return <>
      <motion.nav initial={{
      y: -100
    }} animate={{
      y: 0
    }} transition={{
      duration: 0.5
    }} className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled || menuOpen ? 'bg-primary-dark/95 backdrop-blur-sm py-4 shadow-lg' : 'bg-transparent py-6'}`} data-id="element-116">
        <div className="container mx-auto px-6 flex justify-between items-center" data-id="element-117">
          <Link to="/" className="flex items-center relative z-50" onClick={handleLinkClick} data-id="element-118">
            <img src="https://cdn.magicpatterns.com/uploads/m22cd52VGFAbga9BNLUyHJ/accutemp-logo-white.svg" alt="AccuTemp Air Conditioning — commercial HVAC specialists in Nuneaton" className="h-8 md:h-10 w-auto" data-id="element-119" />
          </Link>

          {/* Desktop nav */}
          <div className="hidden lg:flex items-center gap-8 text-white/90 text-sm font-medium" data-id="element-120">
            {menuLinks.map(link => link.route ? <Link key={link.label} to={link.route} className="hover:text-white transition-colors" data-id="element-121">
                  {link.label}
                </Link> : <a key={link.label} href={getHref(link)} className="hover:text-white transition-colors" data-id="element-122">
                  {link.label}
                </a>)}
          </div>

          <div className="flex items-center gap-6 text-white relative z-50" data-id="element-123">
            <button className="hidden sm:flex items-center gap-2 text-sm font-medium hover:text-white/80 transition-colors" data-id="element-124">
              <Globe size={18} data-id="element-125" />
              <span data-id="element-126">EN</span>
            </button>
            <button onClick={() => setMenuOpen(!menuOpen)} className="hover:text-white/80 transition-colors" aria-label={menuOpen ? 'Close menu' : 'Open menu'} aria-expanded={menuOpen} data-id="element-127">
              {menuOpen ? <X size={28} data-id="element-128" /> : <Menu size={28} data-id="element-129" />}
            </button>
          </div>
        </div>
      </motion.nav>

      {/* Mobile/Full-screen menu overlay */}
      <AnimatePresence data-id="element-130">
        {menuOpen && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.3
      }} className="fixed inset-0 z-40 bg-primary-dark" data-id="element-131">
            <div className="flex flex-col justify-center items-center h-full px-6" data-id="element-132">
              <nav className="flex flex-col items-center gap-2 w-full max-w-sm" data-id="element-133">
                {menuLinks.map((link, index) => <motion.div key={link.label} initial={{
              opacity: 0,
              y: 30
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: 20
            }} transition={{
              duration: 0.4,
              delay: index * 0.07
            }} className="w-full" data-id="element-134">
                    {link.route ? <Link to={link.route} onClick={handleLinkClick} className="block w-full text-center text-3xl md:text-4xl font-serif text-white py-4 hover:text-accent transition-colors" data-id="element-135">
                        {link.label}
                      </Link> : <a href={getHref(link)} onClick={handleLinkClick} className="block w-full text-center text-3xl md:text-4xl font-serif text-white py-4 hover:text-accent transition-colors" data-id="element-136">
                        {link.label}
                      </a>}
                  </motion.div>)}
              </nav>

              {/* Contact info at bottom of menu */}
              <motion.div initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} transition={{
            duration: 0.4,
            delay: 0.4
          }} className="absolute bottom-12 left-6 right-6" data-id="element-137">
                <div className="flex flex-col sm:flex-row items-center justify-between gap-6 text-white/50 text-sm" data-id="element-138">
                  <div className="flex items-center gap-4" data-id="element-139">
                    <a href="https://linkedin.com/company/accutemp" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors" data-id="element-140">
                      LinkedIn
                    </a>
                    <span className="w-1 h-1 rounded-full bg-white/30" data-id="element-141"></span>
                    <span data-id="element-142">Nuneaton, CV10 7RA</span>
                  </div>
                  <a href="/#contact" onClick={handleLinkClick} className="bg-accent hover:bg-accent/90 text-white px-6 py-2.5 rounded-full text-sm font-medium transition-colors" data-id="element-143">
                    Get a free quote
                  </a>
                </div>
              </motion.div>
            </div>
          </motion.div>}
      </AnimatePresence>
    </>;
}